import {
  Button,
  DataGridRow,
  makeStyles,
  mergeClasses,
  Spinner,
  TableCellLayout,
  Tooltip,
  tokens,
  type DataGridRowProps,
} from '@fluentui/react-components'
import {
  CheckmarkRegular,
  DocumentRegular,
  FolderRegular,
  ImageRegular,
  MusicNote2Regular,
  VideoRegular,
} from '@fluentui/react-icons'
import { type ForwardRefExoticComponent, type ReactElement, type RefAttributes } from 'react'

import DataTable, { type TableColumn } from '../../../../../components/DataTable'
import { ErrorMessageBar } from '../../../../../components/ErrorMessageBar'
import { InfoLayout } from '../../../../../components/InfoLayout'
import type { UploadPolicy, UploadWorkflow } from '../../../../../shared/models/UploadPolicy'
import type { WorkspaceItem } from '../../../../../shared/models/WorkspaceItem'
import { useUploadPolicy } from '../../../../../shared/queries/useUploadPolicy'
import { resolveMediaFormat, resolveUploadFormat } from '../../../../../utils/fileWorksapceUploadUtil'
import { WorkspaceItemsGrid, WorkspaceItemsGridTile } from '../WorkspaceItemsGrid'
import WorkspaceItemsView, { type WorkspaceItemsDisplayMode } from '../WorkspaceItemsView'

import { useWorkspaceExplorer } from './providers/workspaceExplorerProvider'

const useStyles = makeStyles({
  table: {
  minWidth: '680px',
  flexGrow: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'hidden',
  '& > .fui-DataGrid': {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    '& > .fui-DataGridBody': {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
  },
},
  folderButton: { justifyContent: 'flex-start', minWidth: 0, maxWidth: '100%', paddingInline: 0 },
  fileName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  selectedRow: {
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': { backgroundColor: tokens.colorBrandBackground2Hover },
  },
  selectionIndicator: {
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    whiteSpace: 'nowrap',
  },
  action: {
    minHeight: '32px',
    minWidth: '32px',
  },
  nameCell: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, minWidth: 0 },
  dragHandle: { flexShrink: 0, cursor: 'grab', touchAction: 'none' },
  draggable: { cursor: 'grab' },
  dragging: { opacity: 0.5 },
  dropTarget: {
    backgroundColor: tokens.colorBrandBackground2,
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: '-2px',
  },
  menuItem: {
    minHeight: '32px',
    minWidth: '32px',
  },
})

const WorkspaceExplorerDataGridRow = DataGridRow as ForwardRefExoticComponent<
  DataGridRowProps<WorkspaceItem> & RefAttributes<HTMLElement>
>

function WorkspaceExplorerItemRow({
  item,
  row,
}: {
  item: WorkspaceItem
  row: ReactElement<DataGridRowProps<WorkspaceItem>>
}) {
  const styles = useStyles()
  const { selectedItem, openItem } = useWorkspaceExplorer()
  const selected = item.type === 'file' && selectedItem?.id === item.id

  return (
    <WorkspaceExplorerDataGridRow
      {...row.props}
      aria-selected={item.type === 'file' ? selected : undefined}
      className={mergeClasses(row.props.className, selected && styles.selectedRow)}
      onClick={() => void openItem(item)}
    />
  )
}

export default function WorkspaceExplorerItemsTable({
  policyWorkflow,
}: {
  policyWorkflow?: UploadWorkflow
}) {
  const { workspaceId, currentParentId } = useWorkspaceExplorer()

  return (
    <WorkspaceItemsView scrollWithinItems>
      {(displayMode) => (
        <WorkspaceExplorerItemsContent
          key={`${workspaceId}:${currentParentId}`}
          policyWorkflow={policyWorkflow}
          displayMode={displayMode}
        />
      )}
    </WorkspaceItemsView>
  )
}

function getItemIcon(item: WorkspaceItem, policy?: UploadPolicy) {
  if (item.type === 'folder') {
    return <FolderRegular />
  }
  const format = resolveMediaFormat(item.name, item.mediaType, policy)
  switch (format?.kind) {
    case 'image':
      return <ImageRegular />
    case 'video':
      return <VideoRegular />
    case 'audio':
      return <MusicNote2Regular />
    default:
      return <DocumentRegular />
  }
}

function WorkspaceExplorerItemsContent({
  policyWorkflow,
  displayMode,
}: {
  policyWorkflow?: UploadWorkflow
  displayMode: WorkspaceItemsDisplayMode
}) {
  const styles = useStyles()
  const { items, selectedItem, openItem, isLoading, isError, refetch } = useWorkspaceExplorer()
  const { data: uploadWorkspacePolicy } = useUploadPolicy(policyWorkflow ?? 'workspaceMedia')
  const visibleItems = policyWorkflow
    ? items.filter(
        (item) =>
          item.type === 'folder' ||
          (uploadWorkspacePolicy !== undefined &&
            item.sizeBytes !== null &&
            item.sizeBytes <= uploadWorkspacePolicy.maxFileSizeBytes &&
            resolveUploadFormat(item.name, item.mediaType, uploadWorkspacePolicy) !== null),
      )
    : items

  const columns: TableColumn<WorkspaceItem>[] = [
    {
      id: 'name',
      header: 'Name',
      compare: (left, right) => left.name.localeCompare(right.name),
      renderCell: (item) => (
        <div className={styles.nameCell}>
          <TableCellLayout media={getItemIcon(item, uploadWorkspacePolicy)} truncate>
            <Tooltip content={item.name} relationship="label">
              <Button
                appearance="subtle"
                className={styles.folderButton}
                aria-pressed={item.type === 'file' ? selectedItem?.id === item.id : undefined}
                onClick={(event) => {
                  event.stopPropagation()
                  void openItem(item)
                }}
              >
                <span className={styles.fileName}>{item.name}</span>
              </Button>
            </Tooltip>
          </TableCellLayout>
          {item.type === 'file' && selectedItem?.id === item.id && (
            <CheckmarkRegular aria-hidden="true" className={styles.selectionIndicator} />
          )}
        </div>
      ),
      minWidth: 320,
    },
  ]

  if (displayMode === 'grid') {
    if (isLoading) {
      return <Spinner label="Loading workspace items..." />
    }
    if (isError) {
      return <ErrorMessageBar title="Failed to load workspace items" onRetry={refetch} />
    }
    if (visibleItems.length === 0) {
      return <InfoLayout title="This folder is empty" message="There are no selectable items to display." />
    }
    return (
      <WorkspaceItemsGrid>
        {[...visibleItems].map((item) => (
          <WorkspaceItemsGridTile
            key={item.id}
            item={item}
            icon={getItemIcon(item, uploadWorkspacePolicy)}
            selected={item.type === 'file' ? selectedItem?.id === item.id : undefined}
            onOpen={openItem}
          />
        ))}
      </WorkspaceItemsGrid>
    )
  }

  return (
    <DataTable<WorkspaceItem>
      sortable
      size="large"
      className={styles.table}
      columns={columns}
      data={visibleItems}
      isLoading={isLoading}
      isError={isError}
      error={<ErrorMessageBar title="Failed to load workspace items" onRetry={refetch} />}
      getRowId={(item) => item.id}
      renderRow={(item, row) => (
        <WorkspaceExplorerItemRow key={item.id} item={item} row={row} />
      )}
      customEmptyMessage={<div></div>}
    />
  )
}
